<script lang="ts">
  import {
    BrandLogo,
    Button,
    Drawer,
    Loader,
    NavBar,
    NavigationMenu
  } from '@tomra/design-system-svelte';
  import { authState } from '$lib/auth/keycloak';
  import { page } from '$app/state';
  import { resolve } from '$app/paths';
  import { isNavigationItemActive, type NavigationItem } from '$lib/navigation';

  let {
    menuItems,
    menuStatus,
    onRetry
  }: {
    menuItems: NavigationItem[];
    menuStatus: 'loading' | 'ready' | 'error';
    onRetry: () => void;
  } = $props();

  let menuOpen = $state(false);

  function logout() {
    void $authState.keycloak?.logout();
  }
</script>

{#snippet navigation(mobile = false)}
  <NavigationMenu
    aria-label={mobile ? 'Mobile navigation' : 'Main navigation'}
    orientation={mobile ? 'vertical' : 'horizontal'}
  >
    {#if menuStatus === 'loading'}
      <div class="flex items-center gap-3" role="status">
        <Loader variant="spinner" aria-hidden="true" role="presentation" />
        <span class="text-sm text-gray-600">Loading navigation...</span>
      </div>
    {:else if menuStatus === 'error'}
      <div class="flex flex-wrap items-center gap-3">
        <p class="text-sm text-rose-700" role="alert">Unable to load navigation.</p>
        <Button size="sm" onclick={onRetry}>
          <span class="icon-[fluent--arrow-clockwise-20-regular] size-4" aria-hidden="true"></span>
          Retry
        </Button>
      </div>
    {:else if menuItems.length === 0}
      <p class="text-sm text-gray-600" role="status">No navigation items available.</p>
    {:else}
      <NavigationMenu.List class={mobile ? 'w-full flex-col items-start' : 'flex-wrap'}>
        {#each menuItems as item, index (`${item.href}:${index}`)}
          <NavigationMenu.Item class={mobile ? 'w-full' : 'shrink-0'}>
            <NavigationMenu.Link
              href={resolve(item.href)}
              active={isNavigationItemActive(item.href, page.url)}
              onclick={() => {
                menuOpen = false;
              }}>{item.title}</NavigationMenu.Link
            >
          </NavigationMenu.Item>
        {/each}
      </NavigationMenu.List>
    {/if}
  </NavigationMenu>
{/snippet}

<div class="relative z-20 shrink-0">
  <NavBar>
    <div class="flex w-full items-center gap-6">
      <a
        href={resolve('/')}
        class="flex shrink-0 items-center gap-3 rounded focus-visible:outline-2 focus-visible:outline-teal-700"
        aria-label="Service Portal home"
      >
        <BrandLogo />
        <span class="hidden text-lg font-medium text-gray-950 sm:block">Service Portal</span>
      </a>
      {#if $authState.status === 'authenticated'}
        <div class="hidden min-w-0 flex-1 xl:block">
          {@render navigation()}
        </div>
        <div class="ml-auto flex shrink-0 items-center gap-3">
          {#if $authState.username}
            <div class="hidden items-center gap-2 sm:flex">
              <span
                class="icon-[fluent--person-circle-24-regular] size-6 text-gray-600"
                aria-hidden="true"
              ></span>
              <span class="max-w-40 truncate text-sm text-gray-600" title={$authState.username}
                >{$authState.username}</span
              >
            </div>
          {/if}
          <Drawer bind:open={menuOpen}>
            <div class="xl:hidden">
              <Drawer.Trigger>
                {#snippet child({ props })}
                  <Button {...props} variant="icon" aria-label="Open navigation">
                    <span class="icon-[fluent--navigation-24-regular] size-6" aria-hidden="true"
                    ></span>
                  </Button>
                {/snippet}
              </Drawer.Trigger>
            </div>
            <Drawer.Portal>
              <Drawer.Overlay />
              <Drawer.Content side="left" class="overflow-y-auto">
                <Drawer.Header>
                  <BrandLogo showText />
                  <Drawer.Title>Service Portal</Drawer.Title>
                  <Drawer.Description>Choose a page to get started.</Drawer.Description>
                </Drawer.Header>
                <div class="mt-6">{@render navigation(true)}</div>
                <Drawer.Close aria-label="Close navigation" />
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer>
          <Button variant="icon" onclick={logout} aria-label="Log out" title="Log out">
            <span class="icon-[fluent--sign-out-24-regular] size-6" aria-hidden="true"></span>
          </Button>
        </div>
      {/if}
    </div>
  </NavBar>
</div>
